"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { Card, CardBody, CardHeader, Badge, Input, Row, Col, FormGroup, Label } from "reactstrap"
import { FaEye, FaPlus, FaSearch, FaTicketAlt, FaCheckCircle, FaClock, FaPhone, FaTimes } from "react-icons/fa"
import DataTable, { type TableColumn } from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import { TicketPaymentService } from "../../../Service/TicketingService"
import type { TicketPayment, PaginatedResponse } from "./types"
import { toast } from "react-toastify"

interface Props {
  onAdd: () => void
  onViewDetails: (payment: TicketPayment) => void
  onVerifyTicket: () => void
  refreshTrigger: boolean
}

interface TicketStatistics {
  total_payments: number
  total_tickets: number
  used_tickets: number
  unused_tickets: number
  usage_percentage: number
}

const TicketPaymentList: React.FC<Props> = ({ onAdd, onViewDetails, onVerifyTicket, refreshTrigger }) => {
  const [payments, setPayments] = useState<TicketPayment[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [statistics, setStatistics] = useState<TicketStatistics | null>(null)
  const [statisticsLoading, setStatisticsLoading] = useState(true)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
  })

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const fetchPayments = async (page = 1, searchTerm = "") => {
    try {
      setLoading(true)
      const response: PaginatedResponse<TicketPayment> = await TicketPaymentService.getAll(
        page,
        pagination.pageSize,
        searchTerm,
      )
      setPayments(response.results)
      setPagination({
        currentPage: response.current_page,
        totalPages: response.total_pages,
        totalCount: response.count,
        pageSize: response.page_size,
      })
    } catch (error) {
      console.error("Failed to fetch ticket payments:", error)
      toast.error("Failed to load ticket payments")
    } finally {
      setLoading(false)
    }
  }

  const fetchStatistics = async () => {
    try {
      setStatisticsLoading(true)
      const response = await TicketPaymentService.getStatistics()
      setStatistics(response)
    } catch (error) {
      console.error("Failed to fetch statistics:", error)
      toast.error("Failed to load statistics")
    } finally {
      setStatisticsLoading(false)
    }
  }

  useEffect(() => {
    fetchPayments()
    fetchStatistics()

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [refreshTrigger])

  // Remove the handleSearch function and replace with live search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearch = e.target.value
    setSearch(newSearch)

    // Debounce the search to avoid too many API calls
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchPayments(1, newSearch)
    }, 300) // 300ms delay for debouncing
  }

  const handleClearSearch = () => {
    setSearch("")
    fetchPayments(1, "")
    toast.info("Search cleared")
  }

  const handlePageChange = (page: number) => {
    fetchPayments(page, search)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPerPage }))
    fetchPayments(page, search)
  }

  const getSMSStatusBadge = (sms_sent: boolean) => {
    return (
      <Badge color={sms_sent ? "success" : "warning"} pill>
        {sms_sent ? "Sent" : "Pending"}
      </Badge>
    )
  }

  // Enhanced phone number formatting
  const formatPhoneNumber = (phone: string) => {
    // Remove any non-digit characters
    const cleaned = phone.replace(/\D/g, "")

    // Format Rwanda phone numbers (e.g., +250 788 123 456)
    if (cleaned.startsWith("250") && cleaned.length === 12) {
      return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`
    } else if (cleaned.length === 9 && cleaned.startsWith("7")) {
      // Local format (e.g., 788 123 456)
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
    }

    return phone // Return original if no pattern matches
  }

  const columns: TableColumn<TicketPayment>[] = [
    
    {
      name: "TX Reference",
      selector: (row) => row.tx_reference,
      sortable: true,
      cell: (row) => <strong >{row.tx_reference}</strong>,
      minWidth: "150px",
    },
    {
      name: "Phone Number",
      selector: (row) => row.phone_number,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <FaPhone className="me-2 text-muted" size={12} />
          <span className="font-monospace">{formatPhoneNumber(row.phone_number)}</span>
        </div>
      ),
      minWidth: "180px",
    },
    {
      name: "Operator",
      selector: (row) => row.operator,
      sortable: true,
      cell: (row) => (
        <Badge color="info" pill>
          {row.operator}
        </Badge>
      ),
      width: "120px",
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
      cell: (row) => <strong className="text-success">FRW {row.amount.toLocaleString()}</strong>,
      width: "130px",
    },
    {
      name: "Tickets",
      selector: (row) => row.num_tickets,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <FaTicketAlt className="me-1 text-primary" />
          <Badge color="secondary" pill>
            {row.num_tickets}
          </Badge>
        </div>
      ),
      width: "100px",
    },
    {
      name: "SMS Status",
      selector: (row) => row.sms_sent,
      sortable: true,
      cell: (row) => getSMSStatusBadge(row.sms_sent),
      width: "120px",
    },
    {
      name: "Created",
      selector: (row) => row.created_at,
      sortable: true,
      cell: (row) => (
        <div>
          <div className="fw-medium">{new Date(row.created_at).toLocaleDateString()}</div>
          <small className="text-muted">{new Date(row.created_at).toLocaleTimeString()}</small>
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Btn color="info" size="sm" onClick={() => onViewDetails(row)} title="View Details">
            <FaEye />
          </Btn>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "100px",
    },
  ]

  // Check if search term looks like a phone number
  const isPhoneSearch = (searchTerm: string) => {
    const cleaned = searchTerm.replace(/\D/g, "")
    return cleaned.length >= 9 || searchTerm.includes("+") || searchTerm.startsWith("07") || searchTerm.startsWith("25")
  }

  return (
    <Card>
      <CardHeader>
        <Row className="align-items-center">
          <Col md="6">
            <h5 className="mb-0">
              <FaTicketAlt className="me-2 text-primary" />
              Ticket Payments ({pagination.totalCount})
            </h5>
            <small className="text-muted">Manage and track ticket payment transactions</small>
          </Col>
          <Col md="6" className="text-end">
            <div className="d-flex gap-2 justify-content-end">
              <Btn color="success" size="sm" onClick={onVerifyTicket}>
                <FaCheckCircle className="me-2" />
                Verify Ticket
              </Btn>
              <Btn color="primary" size="sm" onClick={onAdd}>
                <FaPlus className="me-2" />
                New Payment
              </Btn>
            </div>
          </Col>
        </Row>
      </CardHeader>

      {/* Statistics Cards */}
      <div className="bg-light p-3 border-bottom">
        {statisticsLoading ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm me-2" role="status" />
            Loading statistics...
          </div>
        ) : statistics ? (
          <Row>
            <Col xl="3" md="6" className="mb-3">
              <div className="d-flex align-items-center p-3 bg-white rounded shadow-sm">
                <div className="rounded-circle p-2 me-3 bg-primary bg-opacity-10">
                  <FaTicketAlt className="text-primary" />
                </div>
                <div>
                  <h6 className="text-muted mb-0 small">Total Payments</h6>
                  <h4 className="mb-0 fw-bold">{statistics.total_payments}</h4>
                </div>
              </div>
            </Col>
            <Col xl="3" md="6" className="mb-3">
              <div className="d-flex align-items-center p-3 bg-white rounded shadow-sm">
                <div className="rounded-circle p-2 me-3 bg-info bg-opacity-10">
                  <FaTicketAlt className="text-info" />
                </div>
                <div>
                  <h6 className="text-muted mb-0 small">Total Tickets</h6>
                  <h4 className="mb-0 fw-bold">{statistics.total_tickets}</h4>
                </div>
              </div>
            </Col>
            <Col xl="3" md="6" className="mb-3">
              <div className="d-flex align-items-center p-3 bg-white rounded shadow-sm">
                <div className="rounded-circle p-2 me-3 bg-success bg-opacity-10">
                  <FaCheckCircle className="text-success" />
                </div>
                <div>
                  <h6 className="text-muted mb-0 small">Used Tickets</h6>
                  <h4 className="mb-0 fw-bold">{statistics.used_tickets}</h4>
                </div>
              </div>
            </Col>
            <Col xl="3" md="6" className="mb-3">
              <div className="d-flex align-items-center p-3 bg-white rounded shadow-sm">
                <div className="rounded-circle p-2 me-3 bg-warning bg-opacity-10">
                  <FaClock className="text-warning" />
                </div>
                <div>
                  <h6 className="text-muted mb-0 small">Unused Tickets</h6>
                  <h4 className="mb-0 fw-bold">{statistics.unused_tickets}</h4>
                </div>
              </div>
            </Col>
            <Col md="12">
              <div className="d-flex align-items-center p-3 bg-white rounded shadow-sm">
                <div className="rounded-circle p-2 me-3 bg-secondary bg-opacity-10">
                  <FaSearch className="text-secondary" />
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <h6 className="text-muted mb-0 small">Usage Rate</h6>
                    <Badge
                      color={
                        statistics.usage_percentage > 70
                          ? "success"
                          : statistics.usage_percentage > 40
                            ? "warning"
                            : "danger"
                      }
                    >
                      {statistics.usage_percentage}%
                    </Badge>
                  </div>
                  <div className="progress" style={{ height: "6px" }}>
                    <div className="progress-bar" style={{ width: `${statistics.usage_percentage}%` }} />
                  </div>
                  <div className="d-flex justify-content-between mt-1">
                    <small className="text-muted">{statistics.used_tickets} used</small>
                    <small className="text-muted">{statistics.unused_tickets} remaining</small>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        ) : null}
      </div>

      <CardBody>
        {/* Enhanced Search */}
        <Row className="mb-4">
          <Col md="8">
            <FormGroup>
              <Label className="fw-bold">
                <FaSearch className="me-1" size={12} />
                Search Payments
              </Label>
              <div className="position-relative">
                <Input
                  type="text"
                  placeholder="Search by TX reference, phone number, or operator..."
                  value={search}
                  onChange={handleSearchChange}
                  className="pe-5"
                />
                {search && (
                  <Btn
                    color="link"
                    size="sm"
                    className="position-absolute top-50 end-0 translate-middle-y pe-2"
                    onClick={handleClearSearch}
                    style={{ border: "none", background: "none" }}
                  >
                    <FaTimes size={12} />
                  </Btn>
                )}
              </div>
              <small className="text-muted">
                <FaPhone className="me-1" size={10} />
                {isPhoneSearch(search) && search.length > 0
                  ? "Live search by phone number - supports formats: +250788123456, 0788123456, 788123456"
                  : "Live search by transaction reference, phone number, or mobile operator"}
              </small>
            </FormGroup>
          </Col>
          <Col md="4" className="d-flex align-items-end">
            <FormGroup className="w-100">
              <Btn
                color="secondary"
                size="sm"
                onClick={handleClearSearch}
                className="w-100 d-flex align-items-center justify-content-center"
                disabled={!search}
              >
                <FaTimes className="me-1" size={12} />
                Clear Search
              </Btn>
            </FormGroup>
          </Col>
        </Row>

        {/* Search Results Info */}
        {search && (
          <div className="mb-3">
            <Badge color="info" className="me-2">
              <FaSearch className="me-1" size={10} />
              Searching: "{search}"
            </Badge>
            {isPhoneSearch(search) && (
              <Badge color="success" className="me-2">
                <FaPhone className="me-1" size={10} />
                Phone Search
              </Badge>
            )}
            <small className="text-muted">
              Found {pagination.totalCount} payment{pagination.totalCount !== 1 ? "s" : ""} matching your search
            </small>
          </div>
        )}

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={payments}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={pagination.totalCount}
          paginationDefaultPage={pagination.currentPage}
          paginationPerPage={pagination.pageSize}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent={
            <div className="py-5 text-center text-muted">
              <div className="mb-3">
                <FaTicketAlt size={48} className="opacity-50" />
              </div>
              <h6>No ticket payments found</h6>
              <p className="mb-0">
                {search
                  ? `No payments match your search criteria "${search}"`
                  : "No ticket payments have been recorded yet"}
              </p>
              {search && (
                <Btn color="primary" size="sm" onClick={handleClearSearch} className="mt-3">
                  Clear Search
                </Btn>
              )}
            </div>
          }
          striped
          highlightOnHover
          responsive
          customStyles={{
            headCells: {
              style: {
                fontWeight: "bold",
                backgroundColor: "#f8f9fa",
              },
            },
            rows: {
              highlightOnHoverStyle: {
                backgroundColor: "#f0f0f0",
              },
            },
          }}
        />
      </CardBody>
    </Card>
  )
}

export default TicketPaymentList
